import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  InjectionToken,
  Output,
  computed,
  inject,
  linkedSignal,
  signal,
} from '@angular/core'
import { FormsModule } from '@angular/forms'

import { Store } from '@ngrx/store'
import { first as _first } from 'lodash/fp'

import {
  FLX_MODAL_DATA,
  FlxButtonModule,
  FlxFormControlModule,
  FlxModalModule,
  FlxModalService,
} from '@flowx/angular-ui-toolkit'

import { FlxFileSourceUploadComponent } from './file-source-upload/file-source-upload.component'

import {
  DEFAULT_BPMN_FILE_PREVIEW,
  DEFAULT_IMAGE_FILE_PREVIEW,
} from '../../common/icons/ai-icons.dictionary'
import { ConversationSelectors } from '../../conversation/conversation.selectors'
import { getAvailableUploadOptions } from '../../conversation/conversation.utils'
import { UploadContextFile } from '../../types/ai.types'

export type SourceModalData = {
  action: any
  initialFile?: UploadContextFile
  initialFileType?: 'image' | 'bpmn'
}

@Component({
  selector: 'flx-source-modal',
  imports: [
    CommonModule,
    FormsModule,
    FlxModalModule,
    FlxButtonModule,
    FlxFormControlModule,
    FlxFileSourceUploadComponent,
  ],
  templateUrl: './source-modal.component.html',
  styleUrl: './source-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxSourceModalComponent {
  #modalService = inject(FlxModalService)
  #store = inject(Store)
  #data = inject(FLX_MODAL_DATA as InjectionToken<SourceModalData>)

  #availableUploadOptions = getAvailableUploadOptions(this.#data.action.agent.agent)
  #files = signal<UploadContextFile[]>(this.#data.initialFile ? [this.#data.initialFile] : [])
  #uploadError = signal<string | null>(null)

  @Output() dataEventEmitter = new EventEmitter<{
    fileType: 'image' | 'bpmn'
    file: UploadContextFile
  }>()

  action = this.#data.action
  initialFile = this.#data.initialFile
  initialFileType = this.#data.initialFileType
  selectedTab = linkedSignal<'image' | 'bpmn'>(() => this.#data.initialFileType || 'image')
  tabs = [
    ...(this.#availableUploadOptions.imageUpload
      ? [
          {
            value: 'image',
            label: 'Image',
            disabled: false,
          },
        ]
      : []),
    ...(this.#availableUploadOptions.bpmnUpload
      ? [
          {
            value: 'bpmn',
            label: 'BPMN',
            disabled: false,
          },
        ]
      : []),
  ]
  fileUploadConfig = computed<{
    allowedFileTypes: string[]
    isMultipleUploadAllowed: boolean
    maxFileSize?: number
    uploadButtonText: string
    defaultImage: string
    noFileUploaded?: string
  }>(() => {
    switch (this.selectedTab()) {
      case 'image':
        return {
          allowedFileTypes: this.#store.selectSignal(ConversationSelectors.allowedImageFileTypes)(),
          isMultipleUploadAllowed: this.#store.selectSignal(
            ConversationSelectors.isMultipleImageUploadAllowed
          )(),
          maxFileSize: this.#store.selectSignal(ConversationSelectors.maxAllowedImageFileSize)(),
          uploadButtonText: 'Upload image',
          defaultImage: DEFAULT_IMAGE_FILE_PREVIEW,
          noFileUploaded: 'No image uploaded',
        }
      case 'bpmn':
        return {
          allowedFileTypes: this.#store
            .selectSignal(ConversationSelectors.allowedBpmnFileTypes)()
            .filter(Boolean),
          isMultipleUploadAllowed: false,
          maxFileSize: this.#store.selectSignal(ConversationSelectors.maxAllowedBpmnFileSize)(),
          uploadButtonText: 'Upload .bpmn',
          defaultImage: DEFAULT_BPMN_FILE_PREVIEW,
        }
    }
    return
  })
  hasSelectedSource = computed(() => !!this.#files()?.length && !this.#uploadError())

  save(): void {
    this.dataEventEmitter.emit({
      fileType: this.selectedTab(),
      file: _first(this.#files()),
    })
  }

  onFileSelected(files: UploadContextFile[]): void {
    this.#files.set(files)
  }

  onUploadError(error: string | null): void {
    this.#uploadError.set(error)
  }

  closeModal(): void {
    this.#modalService.hide()
  }
}
